// ============================================================================
// Fleet asset add/edit modals — Vehicle, Floor machine, and Asset cost.
// Overlay primitive: overlayClickClose (no Radix). Numeric blanks coerced to
// null (toNumOrNull/toIntOrNull) so nullable columns stay clean. Writes go
// straight to the base tables (fleet-admin RLS) — no protected-column guards
// on vehicles/floor_machines, and asset_costs is admin-writable.
// ============================================================================
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useToast } from '../../shared/components';
import { overlayClickClose } from '../../shared/overlay-close';
import { toNumOrNull, toIntOrNull } from '../../shared/coerce';
import {
  saveVehicle,
  saveAssetCost,
  type VehicleRow,
  type VehicleWritePatch,
  type MachineRow,
  type CostRow,
} from './hooks/use-fleet-data';
import { VEHICLE_STATUSES } from './vehicle-status';
import { errorMessage } from '../../shared/errors';
import { saveMachineSpec } from './hooks/use-asset-location';

const POWERTRAINS = ['gas', 'hybrid', 'ev', 'phev', 'diesel'];
const COST_CATEGORIES = [
  'fuel', 'charging', 'charger_install', 'maintenance', 'repair', 'toll',
  'registration', 'insurance', 'lease', 'depreciation', 'other',
];

// ----- shared bits ----------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-border-input focus:outline-none';

function ModalShell({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-tcs-border bg-tcs-overlay p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-tcs-text">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4 space-y-3">{children}</div>
        <div className="mt-5 flex justify-end gap-2">{footer}</div>
      </div>
    </div>
  );
}

/**
 * Employee options for the assignee dropdown.
 *
 * ⛔ THIS READ v_am_name UNTIL 2026-08-26. That view is a RESOLVER — it returns
 * every employee at every status so history renders — which meant a vehicle
 * could be assigned to somebody who left in March. Pickers bind to
 * v_employee_picker (Active only). The `currentEid` fallback below still keeps
 * an already-assigned former employee visible on the row so an existing
 * assignment does not silently blank; that is resolution, not a new choice.
 */
function useAssigneeOptions(currentEid: string | null) {
  const [opts, setOpts] = useState<{ eid: string; name: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('v_employee_picker').select('employee_id, display_name').order('display_name');
      if (cancelled) return;
      const list = ((data ?? []) as { employee_id: string; display_name: string | null }[])
        .filter((r) => r.display_name)
        .map((r) => ({ eid: r.employee_id, name: r.display_name as string }));
      if (currentEid && !list.some((o) => o.eid === currentEid)) {
        list.unshift({ eid: currentEid, name: currentEid });
      }
      setOpts(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentEid]);
  return opts;
}

// ----- Vehicle add/edit -----------------------------------------------------

export function VehicleFormModal({
  vehicle,
  onClose,
  onSaved,
}: {
  vehicle: VehicleRow | null;
  onClose: () => void;
  /** Receives the SAVED row. On the add path that is the only way the caller
   *  learns the vehicle_id the trigger just minted. */
  onSaved: (saved: VehicleRow) => void;
}) {
  const isEdit = vehicle != null;
  const { success, error } = useToast();
  const assignees = useAssigneeOptions(vehicle?.assigned_employee_eid ?? null);
  const [f, setF] = useState({
    make_model: vehicle?.make_model ?? '',
    year: vehicle?.year != null ? String(vehicle.year) : '',
    license_plate: vehicle?.license_plate ?? '',
    plate_state: vehicle?.plate_state ?? '',
    powertrain: vehicle?.powertrain ?? '',
    status: vehicle?.status ?? 'Active',
    location: vehicle?.location ?? '',
    assigned_employee_eid: vehicle?.assigned_employee_eid ?? '',
    vin: vehicle?.vin ?? '',
    registration_exp: vehicle?.registration_exp ?? '',
    insurance_provider: vehicle?.insurance_provider ?? '',
    insurance_exp: vehicle?.insurance_exp ?? '',
    emissions_exp: vehicle?.emissions_exp ?? '',
    toll_pass: vehicle?.toll_pass ?? false,
    has_home_charger: vehicle?.has_home_charger ?? false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));

  async function submit() {
    if (!f.make_model.trim()) {
      error('Make/model is required.');
      return;
    }
    setSaving(true);
    try {
      // NOTE: no vehicle_id key, on either path. VehicleWritePatch makes that
      // structural rather than a habit — the type has no such property, so a
      // blank string cannot reach the column even by accident. The BEFORE
      // INSERT trigger trg_vehicles_assign_id mints the VEH-#### id.
      const patch: VehicleWritePatch = {
        make_model: f.make_model.trim(),
        year: toIntOrNull(f.year),
        license_plate: f.license_plate.trim() || null,
        plate_state: f.plate_state.trim() || null,
        powertrain: f.powertrain || null,
        status: f.status.trim() || null,
        location: f.location.trim() || null,
        assigned_employee_eid: f.assigned_employee_eid || null,
        vin: f.vin.trim() || null,
        registration_exp: f.registration_exp || null,
        insurance_provider: f.insurance_provider.trim() || null,
        insurance_exp: f.insurance_exp || null,
        emissions_exp: f.emissions_exp || null,
        toll_pass: f.toll_pass,
        has_home_charger: f.has_home_charger,
      };
      const saved = await saveVehicle(vehicle?.vehicle_id ?? null, patch);
      success(isEdit ? 'Vehicle updated.' : `Vehicle added as ${saved.vehicle_id}.`);
      onSaved(saved);
      onClose();
    } catch (e) {
      error(errorMessage(e, 'Could not save the vehicle.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      title={isEdit ? `Edit ${vehicle?.make_model ?? 'vehicle'}` : 'Add vehicle'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-surface">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="rounded-md bg-tcs-inverse px-3 py-2 text-sm font-medium text-tcs-inverse-ink disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Field label="Make / model"><input className={inputCls} value={f.make_model} onChange={(e) => set('make_model', e.target.value)} /></Field></div>
        <Field label="Year"><input type="number" className={inputCls} value={f.year} onChange={(e) => set('year', e.target.value)} /></Field>
        <Field label="Powertrain">
          <select className={inputCls} value={f.powertrain} onChange={(e) => set('powertrain', e.target.value)}>
            <option value="">—</option>
            {POWERTRAINS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Plate"><input className={inputCls} value={f.license_plate} onChange={(e) => set('license_plate', e.target.value)} /></Field>
        <Field label="Plate state"><input className={inputCls} value={f.plate_state} onChange={(e) => set('plate_state', e.target.value)} maxLength={2} /></Field>
        <Field label="Status">
          {/* One vocabulary, three readers: this form, the rollup filter, and
              the card's status sheet all read VEHICLE_STATUSES. It was a
              free-text box until 2026-08-24, which is how 'Active - Reg Needed'
              got into a column with no CHECK constraint. That row is preserved,
              not rewritten — an off-list current value is offered back so
              editing anything else about the vehicle does not silently change
              its status. */}
          <select className={inputCls} value={f.status} onChange={(e) => set('status', e.target.value)}>
            {!(VEHICLE_STATUSES as readonly string[]).includes(f.status) && f.status !== '' && (
              <option value={f.status}>{f.status} (existing)</option>
            )}
            {VEHICLE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Region / location"><input className={inputCls} value={f.location} onChange={(e) => set('location', e.target.value)} /></Field>
        <div className="col-span-2">
          <Field label="Assigned to">
            <select className={inputCls} value={f.assigned_employee_eid} onChange={(e) => set('assigned_employee_eid', e.target.value)}>
              <option value="">Unassigned</option>
              {assignees.map((o) => <option key={o.eid} value={o.eid}>{o.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-span-2"><Field label="VIN"><input className={inputCls} value={f.vin} onChange={(e) => set('vin', e.target.value)} /></Field></div>
        <Field label="Registration expires"><input type="date" className={inputCls} value={f.registration_exp} onChange={(e) => set('registration_exp', e.target.value)} /></Field>
        <Field label="Insurance expires"><input type="date" className={inputCls} value={f.insurance_exp} onChange={(e) => set('insurance_exp', e.target.value)} /></Field>
        <Field label="Insurance provider"><input className={inputCls} value={f.insurance_provider} onChange={(e) => set('insurance_provider', e.target.value)} /></Field>
        <Field label="Emissions expires (CO)"><input type="date" className={inputCls} value={f.emissions_exp} onChange={(e) => set('emissions_exp', e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm text-tcs-text"><input type="checkbox" checked={f.toll_pass} onChange={(e) => set('toll_pass', e.target.checked)} /> Toll pass</label>
        <label className="flex items-center gap-2 text-sm text-tcs-text"><input type="checkbox" checked={f.has_home_charger} onChange={(e) => set('has_home_charger', e.target.checked)} /> Home charger</label>
      </div>
    </ModalShell>
  );
}

// ----- Floor machine add/edit ----------------------------------------------

export function MachineFormModal({
  machine,
  onClose,
  onSaved,
}: {
  machine: MachineRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = machine != null;
  const { success, error } = useToast();
  const assignees = useAssigneeOptions(machine?.assigned_employee_eid ?? null);
  const [f, setF] = useState({
    machine_brand: machine?.machine_brand ?? '',
    machine_model: machine?.machine_model ?? '',
    serial_number: machine?.serial_number ?? '',
    assigned_employee_eid: machine?.assigned_employee_eid ?? '',
    last_maintenance_date: machine?.last_maintenance_date ?? '',
    next_maintenance_due: machine?.next_maintenance_due ?? '',
    warranty_exp: machine?.warranty_exp ?? '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  async function submit() {
    if (!f.machine_brand.trim() && !f.machine_model.trim()) {
      error('Enter a brand or model.');
      return;
    }
    setSaving(true);
    try {
      // RPC, not a table write. floor_machines revokes INSERT/UPDATE from
      // `authenticated` in this same PR, so a direct .update() here would fail
      // on privilege. The RPC also cannot touch location or status — those have
      // their own RPCs that write a movement event alongside the projection.
      const msg = await saveMachineSpec({
        machineId: machine?.machine_id ?? null,
        brand: f.machine_brand.trim() || null,
        model: f.machine_model.trim() || null,
        serial: f.serial_number.trim() || null,
        assignedEid: f.assigned_employee_eid || null,
        lastMaintenance: f.last_maintenance_date || null,
        nextMaintenance: f.next_maintenance_due || null,
        warrantyExp: f.warranty_exp || null,
      });
      success(msg);
      onSaved();
      onClose();
    } catch (e) {
      error(errorMessage(e, 'Could not save the equipment.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      title={isEdit ? 'Edit equipment' : 'Add equipment'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-surface">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="rounded-md bg-tcs-inverse px-3 py-2 text-sm font-medium text-tcs-inverse-ink disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Brand"><input className={inputCls} value={f.machine_brand} onChange={(e) => set('machine_brand', e.target.value)} /></Field>
        <Field label="Model"><input className={inputCls} value={f.machine_model} onChange={(e) => set('machine_model', e.target.value)} /></Field>
        <Field label="Serial #"><input className={inputCls} value={f.serial_number} onChange={(e) => set('serial_number', e.target.value)} /></Field>
        {/* Location and Status are deliberately NOT here. This modal edits
            what a machine IS; where it is and whether it works are observations
            that write a movement event, so they live on the equipment detail
            page behind Move / Report broken. A free-text Location box in this
            modal is what made facility_location drift from facility_id in the
            first place — it was the only place the app ever wrote a location,
            and it wrote the wrong column. */}
        <div className="col-span-2 rounded-lg border border-st-info-border bg-st-info-bg px-3 py-2 text-[12.5px] text-st-info-fg-strong">
          {isEdit
            ? 'Location and status are changed from the machine’s page, so the move is recorded with who and when.'
            : 'Add the machine here, then set its location from its page — that records the first sighting.'}
        </div>
        <div className="col-span-2">
          <Field label="Assigned to">
            <select className={inputCls} value={f.assigned_employee_eid} onChange={(e) => set('assigned_employee_eid', e.target.value)}>
              <option value="">Unassigned</option>
              {assignees.map((o) => <option key={o.eid} value={o.eid}>{o.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Last maintenance"><input type="date" className={inputCls} value={f.last_maintenance_date} onChange={(e) => set('last_maintenance_date', e.target.value)} /></Field>
        <Field label="Next maintenance"><input type="date" className={inputCls} value={f.next_maintenance_due} onChange={(e) => set('next_maintenance_due', e.target.value)} /></Field>
        <Field label="Warranty expires"><input type="date" className={inputCls} value={f.warranty_exp} onChange={(e) => set('warranty_exp', e.target.value)} /></Field>
      </div>
    </ModalShell>
  );
}

// ----- Asset cost add/edit --------------------------------------------------

export function AssetCostModal({
  cost,
  vehicleId,
  machineId,
  onClose,
  onSaved,
}: {
  cost: CostRow | null;
  /** Target asset when adding (exactly one of these). */
  vehicleId?: string;
  machineId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = cost != null;
  const { success, error } = useToast();
  const [f, setF] = useState({
    cost_category: cost?.cost_category ?? 'fuel',
    amount: cost?.amount != null ? String(cost.amount) : '',
    incurred_on: cost?.incurred_on ?? new Date().toISOString().slice(0, 10),
    vendor: cost?.vendor ?? '',
    region: cost?.region ?? '',
    is_capital: cost?.is_capital ?? false,
    needs_review: cost?.needs_review ?? false,
    note: cost?.note ?? '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));

  async function submit() {
    const amt = toNumOrNull(f.amount);
    if (amt == null) {
      error('Enter an amount.');
      return;
    }
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {
        cost_category: f.cost_category,
        amount: amt,
        incurred_on: f.incurred_on,
        vendor: f.vendor.trim() || null,
        region: f.region.trim() || null,
        is_capital: f.is_capital,
        needs_review: f.needs_review,
        note: f.note.trim() || null,
      };
      if (!isEdit) {
        patch.vehicle_id = vehicleId ?? null;
        patch.machine_id = machineId ?? null;
        patch.source = 'manual';
      }
      await saveAssetCost(cost?.cost_id ?? null, patch);
      success(isEdit ? 'Cost updated.' : 'Cost added.');
      onSaved();
      onClose();
    } catch (e) {
      error(errorMessage(e, 'Could not save the cost.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      title={isEdit ? 'Edit cost' : 'Add cost'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-surface">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="rounded-md bg-tcs-inverse px-3 py-2 text-sm font-medium text-tcs-inverse-ink disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select className={inputCls} value={f.cost_category} onChange={(e) => set('cost_category', e.target.value)}>
            {COST_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Amount (USD)"><input type="number" step="0.01" className={inputCls} value={f.amount} onChange={(e) => set('amount', e.target.value)} /></Field>
        <Field label="Incurred on"><input type="date" className={inputCls} value={f.incurred_on} onChange={(e) => set('incurred_on', e.target.value)} /></Field>
        <Field label="Vendor"><input className={inputCls} value={f.vendor} onChange={(e) => set('vendor', e.target.value)} /></Field>
        <Field label="Region"><input className={inputCls} value={f.region} onChange={(e) => set('region', e.target.value)} /></Field>
        <div className="col-span-2"><Field label="Note"><input className={inputCls} value={f.note} onChange={(e) => set('note', e.target.value)} /></Field></div>
        <label className="flex items-center gap-2 text-sm text-tcs-text"><input type="checkbox" checked={f.is_capital} onChange={(e) => set('is_capital', e.target.checked)} /> Capital cost</label>
        <label className="flex items-center gap-2 text-sm text-tcs-text"><input type="checkbox" checked={f.needs_review} onChange={(e) => set('needs_review', e.target.checked)} /> Needs review</label>
      </div>
    </ModalShell>
  );
}
